'use client';

/**
 * StandardFooter Component - The original Footer configuration
 */

import Link from 'next/link';
import { useThemePath } from '@/themes/core/hooks';

import { Facebook, Instagram } from 'lucide-react';

import { Menu } from '@/types/navigation';
import { SiteData } from '../../types';

function getAbsoluteUrl(url: string) {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    return `https://${url}`;
}

function FooterItem({ item, getLink, depth = 0 }: { item: any, getLink: (path: string) => string, depth?: number }) {
    return (
        <>
            <li>
                <Link
                    href={getLink(item.url || item.path)}
                    className="hover:text-foreground transition-colors hover:underline underline-offset-4 decoration-primary/30 py-1 block"
                >
                    {item.title}
                </Link>
            </li>
            {item.children && item.children.length > 0 && (
                <ul className="flex flex-col gap-1">
                    {item.children.map((child: any) => (
                        <FooterItem key={child.id || child.title} item={child} getLink={getLink} depth={depth + 1} />
                    ))}
                </ul>
            )}
        </>
    );
}

interface StandardFooterProps {
    site: SiteData;
    customMenus?: Menu[];
    footerSettings: any;
}

export function StandardFooter({ site, customMenus = [], footerSettings }: StandardFooterProps) {
    const { homeLink, getLink } = useThemePath();
    const siteName = site?.name || 'المتجر';
    const logo = site?.logo_url;
    const socialLinks = site?.social_links;
    const contactInfo = site?.contact_info;
    const direction = footerSettings?.footer_direction || 'auto';

    const currentYear = new Date().getFullYear();
    const copyrightText = footerSettings?.copyright || `© ${currentYear} ${siteName}. جميع الحقوق محفوظة.`;

    // Process Menus
    const selectedMenuIds = footerSettings?.footer_menus || [];
    const footerMenus = selectedMenuIds.map((id: string) => {
        // Find by ID, handle, or slug
        return customMenus.find(m => m.id === id || m.handle === id || (m as any).slug === id);
    }).filter(Boolean);


    return (
        <footer
            className="text-sm text-muted-foreground bg-background border-t border-border"
            dir={direction === 'auto' ? undefined : (direction as any)}
        >
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-6 py-12 text-sm md:flex-row md:gap-12 md:px-4 min-[1320px]:px-0">
                {/* Logo & Site Name & Socials */}
                <div className="flex flex-col gap-4 min-w-[200px]">
                    <Link className="flex items-center gap-2 text-foreground md:pt-1" href={homeLink()}>
                        {logo ? (
                            <img src={logo} alt={siteName} className="h-8 w-auto" width={128} height={32} />
                        ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-foreground text-sm font-bold">
                                {siteName.charAt(0)}
                            </div>
                        )}
                        <span className="uppercase font-bold tracking-tight">{siteName}</span>
                    </Link>

                    {/* Site Description (Optional in Standard, but good for branding) */}
                    {site?.description && (
                        <p className="text-xs text-muted-foreground max-w-[250px] leading-relaxed">
                            {site.description}
                        </p>
                    )}

                    {/* Social Links */}
                    {footerSettings?.showSocial !== false && socialLinks && (
                        <div className="flex gap-4 mt-2">
                            {socialLinks.facebook && (
                                <a href={getAbsoluteUrl(socialLinks.facebook)} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-[#1877F2] transition-colors">
                                    <Facebook className="h-5 w-5" />
                                </a>
                            )}
                            {socialLinks.instagram && (
                                <a href={getAbsoluteUrl(socialLinks.instagram)} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-[#E1306C] transition-colors">
                                    <Instagram className="h-5 w-5" />
                                </a>
                            )}
                            {socialLinks.tiktok && (
                                <a href={getAbsoluteUrl(socialLinks.tiktok)} target="_blank" rel="noreferrer" aria-label="TikTok" className="text-muted-foreground hover:text-[#ff0050] transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-5 w-5">
                                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                    </svg>
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {/* Dynamic Footer Menus */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 flex-1">
                    {footerMenus.map((menu: any) => (
                        <div key={menu.id} className="flex flex-col gap-4">
                            <h3 className="font-bold text-foreground text-sm">{menu.title}</h3>
                            <ul className="flex flex-col gap-2">
                                {menu.items?.map((item: any) => (
                                    <FooterItem key={item.id || item.title} item={item} getLink={getLink} />
                                ))}
                            </ul>
                        </div>
                    ))}
                    {/* Fallback if no menus selected */}
                    {footerMenus.length === 0 && (
                        <div className="col-span-full py-4 text-xs italic opacity-50">
                            قم باختيار قوائم لتظهر هنا في إعدادات الثيم.
                        </div>
                    )}
                </div>

                {/* Powered by / Contact / Newsletter */}
                <div className="flex flex-col gap-6 md:min-w-[200px]">
                    {/* Newsletter */}
                    {(footerSettings?.showNewsletter ?? true) && (
                        <div className="flex flex-col gap-3">
                            <h4 className="font-bold text-sm text-foreground">اشترك في النشرة البريدية</h4>
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    placeholder="البريد الإلكتروني"
                                    className="flex-1 px-3 py-2 text-xs border border-border rounded-md bg-transparent focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                                />
                                <button className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-xs font-bold hover:opacity-90 transition-opacity">
                                    →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Contact Snippet */}
                    {(contactInfo?.email || contactInfo?.phone) && (
                        <div className="flex flex-col gap-1 text-xs">
                            {contactInfo.email && <a href={`mailto:${contactInfo.email}`} className="hover:text-foreground transition-colors">{contactInfo.email}</a>}
                            {contactInfo.phone && <a href={`tel:${contactInfo.phone}`} className="hover:text-foreground transition-colors">{contactInfo.phone}</a>}
                        </div>
                    )}
                </div>
            </div>

            {/* Copyright Bar */}
            <div className="border-t border-border py-6 text-xs bg-muted/10">
                <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-4 px-6 md:flex-row md:justify-between md:px-4 min-[1320px]:px-0">
                    <p className="order-2 md:order-1">
                        {copyrightText}
                    </p>

                    {(footerSettings?.showBranding !== false) && (
                        <div className="flex items-center gap-6 order-1 md:order-2">
                            <a
                                className="flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-all font-medium"
                                href="https://ecommaps.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span className="text-[10px]">مدعوم من</span>
                                <img
                                    src="/logo-light.png"
                                    alt="Ecommaps"
                                    className="h-4 w-auto brightness-0 dark:brightness-200 grayscale-0"
                                    width={64}
                                    height={16}
                                />
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
}
